import * as React from "react";
import { FunctionComponent, useContext } from "react";
import { SliderItemProps, SliderItemOnInteractInnerProps } from "./interfaces";
import {
  styled,
  Block,
  Icon,
  Select,
  AsyncSelect,
  Button,
  SelectMenuComponent,
  IBlockProps,
} from "../../../seenspire-library";
import {
  AspectRatioBox,
  SliderItemOnHoverContainer,
  SliderItemOnHoverContent,
  ItemOnHoverDescriptionContainer,
  ItemOnHoverDescriptionContainerInner,
  ItemOnHoverBackgroundContainer,
  SliderItemOnHoverContentInner,
  CONTENT_SPACING,
  ItemText,
  DescriptionText,
} from "./Styles";
import AsyncImage from "./AsyncImage";
import {
  generateSourcesOptions,
  CustomSelectStyles,
  SourcesSelectComponents,
  TransportSelectComponents,
  LineSeparator,
  RegionLanguageSelectComponents,
  LanguageSelectComponents,
  TimeZoneSelectComponents,
  loadAsyncStockOptions,
  loadAsyncPlacesOptions,
  replaceCountry,
  RegionSelectComponents,
} from "./SliderItemOnInteractLogic";
import { useTranslation } from "react-i18next";
import SliderSubmitButton from "./SliderSubmitButton";
import { VideoContainer } from "./VideoItems";
import { get, isArray, size, chain, find } from "lodash";
import playIconFull from "../../../assets/playIconFull.svg";
import { AppContext } from "../../state";
import SliderUnlockButton from "./SliderUnlockButton";
import { SEENSPIRE_APP_IDS } from "../../backend/consts";
import { SCALE_AMOUNT } from "./variables";
import { OptionWithMedia } from "../../modals/AddFeedModal/AddFeedModal";
import { generateTransportItems } from "./helpers/generateTransportItems";
import { generateDefaultCustomFields } from "./SliderItemOnInteractConfig";
import { FeedType, isFeedType } from "../../util";

const SelectIcon = styled(Icon)`
  width: 24px;
  height: 24px;
`;

const getDestinationOrderPrefix = (number: number = 1) => {
  switch (true) {
    case number === 2:
      return "Second";
    case number === 3:
      return "Third";
    case number === 4:
      return "Forth";
    default:
      return "First";
  }
};
const ExpandArrowContainer = styled(Block)`
  cursor: pointer;
  position: absolute;
  bottom: 0;
  z-index: 20;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const PinBlock = styled(Block)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 10px;
  z-index: 1000;
`;

const Hr = styled.hr`
  border: none;
  border-top: 1px solid #dfdfe0;
  width: 100%;
`;

const AddMoreRowsButton = styled(Block)`
  color: ${({ theme }) => theme.colors.cta.base};
  font-size: 9px;
  font-weight: 600;
  line-height: 14px;
  text-align: left;
  padding-left: 5px;
  white-space: nowrap;
  display: grid;
  grid-auto-flow: column;
  grid-gap: 5px;
  justify-self: start;
  align-items: center;
  text-transform: uppercase;
`;

const generateDestination = ({ prev, long, lat, name }: any) => {
  return {
    from: { long: prev.long, lat: prev.lat, name: prev.name },
    to: { long, lat, name },
  };
};

const CustomSelectContainer: FunctionComponent<IBlockProps> = (props) => (
  <Block
    display="grid"
    alignItems="center"
    gridGap={10}
    gridTemplate={{ columns: `1fr` }}
    {...props}
  >
    {props.children}
  </Block>
);

const SliderActiveItem: FunctionComponent<
  SliderItemProps & SliderItemOnInteractInnerProps
> = (props) => {
  const {
    channel,
    isActive,
    isFirst,
    isLast,
    isOpen,
    ignoreConfig,
    onOpenItem,
    data = { source: {} },
    onSourceChange,
    selectedRegionLanguage,
    regionLanguageList,
    localWeatherLanguageList,
    selectedLanguage,
    selectedTimeZone,
    onRegionLanguageChange,
    onNationalRegionLanguageChange,
    onTimezoneChange,
    onLanguageChange,
    category,
    setCustomFields,
    customFields,
    setApiCityLanguageList,
    transportList,
    setApiTransportList,
    setChannelInPreview,
    selectedPlace,
    onPlaceChange,
    showAddContentOnly,
    hoverDirection,
  } = props;

  const { hasPermission, hasFeature, client, hasTrialEnded, hasTrialStarted } =
    useContext(AppContext);
  const { t } = useTranslation();

  const timezonesList = React.useMemo(
    () => [
      {
        label: "Europe (GMT +1)",
        slug: "EUROPEBRUSSELS",
        value: "Europe-brussels",
      },
      {
        label: "USA (GMT -8)",
        slug: "AMERICALOSANGELES",
        value: "America-Los_Angeles",
      },
      {
        label: "Australia (GMT +10)",
        slug: "AUSTRALIASYDNEY",
        value: "Australia-sydney",
      },
    ],
    []
  );

  const isPrecurated = channel.precurated_template;
  const isTraffic = channel.slug === "traffic";
  const isTransportInfo = channel.slug === "here_board";
  const isPublicTransport =
    channel.slug === "public_transport" || channel.slug === "here_board";
  const isFamousBirthday = channel.slug === "dailies_famousbirthdays";
  const isTodayInHistory = channel.slug === "dailies_todayinhistory";
  const isStocks = channel.slug === "datacall_stocks";
  const isAirQuality = channel.slug === "airquality";
  // The discovery catalog channel (from Open.getChannels) doesn't reliably
  // carry presentation_design, so match on the stable slug first - the way the
  // other feed checks above do - and fall back to the presentation design for
  // contexts where it is present (e.g. an already-created feed). See SEE-436.
  const isTube =
    channel.slug === "transportforlondon_tubes" ||
    get(channel, "presentation_design.slug") === "TUBE_TRANSIT";

  const isLocalWeather =
    typeof channel.slug === "string" && channel.slug.includes("local_weather");
  const isNationalWeather =
    typeof channel.slug === "string" && channel.slug.includes("weather_cities");

  const isCustom =
    !isTraffic &&
    !isStocks &&
    !isAirQuality &&
    !isPrecurated &&
    !isLocalWeather &&
    !isNationalWeather;

  const useLanguageRegionDropDown =
    typeof channel.slug === "string" &&
    [
      "famous_quotes",
      "intertainment_healthvision",
      "entertainment_destinations",
      "dailies_todayinhistory",
      "dailies_famousbirthdays",
      "dailies_trivia",
      "dailies_horoscope",
      "dailies_thoughtoftheday",
      "entertainment_vintagehollywood",
      "tips_eco",
      "tips_warehouse",
      "tips_cybersec",
      "health-tips",
      "tips_productivity",
      "european-esg-pulse",
      "global-esg-pulse",
    ].includes(channel.slug);

  const showSources =
    (isCustom &&
      ![
        "weather",
        "local",
        "DAILES",
        "ECONOMY",
        "RECENT",
        "ENTERTAINMENT",
        "FOOTBALLLEAGUES",
        "NORTHAMERICANSPORTS",
        "NATUREAMBIANCE",
        "GLOBAL",
      ].includes(category.slug) &&
      ![
        "dailies_thoughtoftheday",
        "sea_life",
        "digitalart",
        "tips_eco",
        "tips_warehouse",
        "tips_cybersec",
        "tips_productivity",
        "health-tips",
        "intertainment_healthvision",
        "digitalflora",
        "particle_mosaic",
        "luminous_fusion",
        "painted_dreamscape",
        "european-esg-pulse",
        "global-esg-pulse",
      ].includes(channel.slug)) ||
    (["RECENT"].includes(category.slug) &&
      ["news_motoring", "news_uplifting"].includes(channel.slug)) ||
    (["ENTERTAINMENT"].includes(category.slug) &&
      ["entertainment_oddlyenough", "news_entertainment"].includes(
        channel.slug
      ));

  const disableSources = size(channel.available_sources) === 1;

  const showRegions =
    isCustom &&
    ![
      "ECONOMY",
      "FOOTBALLLEAGUES",
      "NORTHAMERICANSPORTS",
      "NATUREAMBIANCE",
    ].includes(category.slug) &&
    !["sea_life", "digitalart"].includes(channel.slug);
  const disableRegions = size(regionLanguageList) === 1;

  const onTransportInfoChange = async (lat: string, long: string) => {
    try {
      const results = await client.Here.cgetTransports(`${lat}%2C${long}`);
      if ((results as any).data) {
        const modes = (results as any).data.flatMap(generateTransportItems);
        setCustomFields((prev: any) =>
          prev ? { ...prev, lat, long } : { lat, long }
        );

        setApiTransportList(modes);
        if (modes.length > 0) {
          onPlaceChange(modes[0]);
        }
      }
    } catch (e: unknown) {
      console.log(e);
    }
  };

  const onNationalWeatherChange = async (value: any) => {
    if (!value) return;
    onNationalRegionLanguageChange(value);
    fetch(
      `https://api.countrylayer.com/v2/name/${value.label}?access_key=8f035ba86fc2a94b4c169a650d4150bf&FullText=`
    )
      .then((results) => results.json())
      .then((data) => {
        if (data && data[0] && data[0].languages) {
          setApiCityLanguageList(data[0].languages);
          setCustomFields((prev: any) =>
            prev
              ? { ...prev, languages: data[0].languages }
              : { languages: data[0].languages }
          );
        }
      });
  };

  React.useEffect(() => {
    if (isNationalWeather) {
      fetch(
        `https://api.countrylayer.com/v2/name/United%20States?access_key=8f035ba86fc2a94b4c169a650d4150bf&FullText=`
      )
        .then((results) => results.json())
        .then((data) => {
          if (data && data[0] && data[0].languages) {
            setApiCityLanguageList(data[0].languages);
            setCustomFields((prev: any) =>
              prev
                ? { ...prev, languages: data[0].languages }
                : { languages: data[0].languages }
            );
          }
        });
    }
  }, [isNationalWeather]);

  if (!isActive) return null;

  const getToShowSourcesForRecentFeed = () => {
    // The Tube status feed (TUBE_TRANSIT) is GLOBAL / English only and exposes
    // no selectable source, so neither the SOURCE nor REGION dropdown offers a
    // real choice. Hide both (and the separator between them). See SEE-436.
    if (isTube) return false;
    if (category.slug !== "RECENT") return showSources;

    const sourceNamesToShow = ["STT", "APMEDIA", "AFPV2", "Reuters", "SkyNewsArabia"];
    const sourceNamesToHide = ["SeenSpire"];

    const isShowSources =
      channel.channel_categories.some(
        (category) => category.slug === "RECENT"
      ) &&
      channel.available_sources.some(
        (sourceItem) =>
          !sourceItem.hidden &&
          sourceItem.source?.active &&
          !sourceNamesToHide.includes(sourceItem.source?.title) &&
          sourceNamesToShow.includes(sourceItem.source?.title)
      );

    return isShowSources;
  };

  const getToShowRegionForRecentFeed = () => {
    // See the SOURCE note above: the Tube feed is GLOBAL / English only, so the
    // REGION dropdown is a no-op. Hide it. See SEE-436.
    if (isTube) return false;
    if (category.slug !== "RECENT") return showRegions;

    const excludedCategorySlugs = [
      "ECONOMY",
      "FOOTBALLLEAGUES",
      "NORTHAMERICANSPORTS",
      "NATUREAMBIANCE",
      "DIGITALART", // added digital art to exclude
    ];

    const excludedChannelSlugs = ["sea_life", "digitalart"];

    const isShowRegion =
      channel?.channel_categories.some(
        (category) => category.slug === "RECENT"
      ) &&
      !excludedCategorySlugs.some((excludedSlug) =>
        channel.channel_categories.some(
          (category) => category.slug === excludedSlug
        )
      ) &&
      !excludedChannelSlugs.includes(channel?.slug);

    return isShowRegion;
  };

  const featureToCheck = channel.type ? channel.type.slug as FeedType : "INFOTAINMENT";

  if (channel?.title === "Global ESG Pulse") {
  console.log("isDisabled", isTraffic &&
    !("place_id" in props.data?.custom_fields) &&
    props.status !== "DONE"
      ? false
      : isLocalWeather &&
        !("place_id" in props.data?.custom_fields) &&
        props.status !== "DONE"
      ? false
      : isPublicTransport &&
        !("place" in props.data?.custom_fields) &&
        props.status !== "DONE"
      ? false
      : (isPublicTransport ||
          isNationalWeather ||
          isTransportInfo) &&
        props.status !== "IN_PROGRESS"
      ? true
      : props.canSubmit
    );
    console.log("isTraffic", isTraffic);
    console.log("isLocalWeather", isLocalWeather);
    console.log("custom_fields", props.data?.custom_fields);
    console.log("status", props.status);
    console.log("canSubmit", props.canSubmit);
  }

  return (
    <>
      <SliderItemOnHoverContainer>
        <SliderItemOnHoverContent
          isActive={isActive}
          isFirst={isFirst}
          isLast={isLast}
          isOpened={isOpen}
          hoverDirection={hoverDirection}
        >
          <ItemOnHoverDescriptionContainer
            isActive={isActive}
            isOpened={isOpen}
          >
            <ItemOnHoverDescriptionContainerInner
              display="grid"
              justifyContent="center"
              alignContent="center"
              justifyItems="center"
              alignItems="center"
              gridGap={10}
            >
              {/* <LockSign channel={channel} /> */}

              {!showAddContentOnly && (
                <Block
                  cursor="pointer"
                  onClick={() => setChannelInPreview(channel)}
                >
                  <img
                    alt=""
                    style={{ width: 54, height: 54 }}
                    src={playIconFull}
                  />
                </Block>
              )}
              <ItemText
                cursor="default"
                color="white"
                fontWeight="bold"
                fontSize="largest"
                textTransform="capitalize"
                padding={{ bottom: 5 }}
              >
                {channel.title}
              </ItemText>
              <DescriptionText
                style={{ lineHeight: "14px" }}
                cursor="default"
                color="white"
                fontWeight="bold"
                margin={{ top: !ignoreConfig && !isOpen ? -10 : 0 }}
                padding={{ right: 20, left: 20 }}
              >
                {(channel as any).description_short}
              </DescriptionText>
            </ItemOnHoverDescriptionContainerInner>

            {!ignoreConfig &&
              !isOpen &&
              hasPermission("FEED.CREATE_ADD_FEED.MANAGE") && (
                <ExpandArrowContainer
                  id={SEENSPIRE_APP_IDS.SLIDER_ITEM_EXPAND_ARROW}
                  onClick={() => {
                    if (isFamousBirthday || isTodayInHistory) {
                      setCustomFields({
                        ...customFields,
                        timezone: timezonesList[0].value,
                      });
                    }
                    if (isStocks) {
                      setCustomFields(generateDefaultCustomFields(channel));
                    }
                    onOpenItem(channel, data);
                  }}
                >
                  <Icon color="white" iconFill="white" name="longArrowDown" />
                </ExpandArrowContainer>
              )}

            <ItemOnHoverBackgroundContainer>
              <AsyncImage
                overlayDirection="bottom"
                src={get(channel, "media[0].thumb.url")}
                rotating={true}
                media={channel.media}
                isActive={isActive}
              ></AsyncImage>
            </ItemOnHoverBackgroundContainer>

            {!showAddContentOnly && <VideoContainer></VideoContainer>}

            <AspectRatioBox />
          </ItemOnHoverDescriptionContainer>

          <SliderItemOnHoverContentInner isActive={isActive}>
            <Block
              display="grid"
              gridGap={CONTENT_SPACING}
              padding={{ all: CONTENT_SPACING }}
            >
              {hasFeature(featureToCheck) ||
              (!hasFeature(featureToCheck) &&
                hasTrialStarted() &&
                !hasTrialEnded()) ? (
                <>
                  {/* Sources */}
                  {!showAddContentOnly && getToShowSourcesForRecentFeed() && !isPublicTransport && (
                    <Select
                      ignorePadding
                      isDisabled={disableSources}
                      value={{ value: data.source } as any}
                      options={generateSourcesOptions(channel)}
                      onChange={onSourceChange}
                      styles={CustomSelectStyles as any}
                      components={SourcesSelectComponents as any}
                    />
                  )}

                  {!showAddContentOnly && getToShowSourcesForRecentFeed() &&
                    getToShowRegionForRecentFeed() && <LineSeparator />}

                  {/* Regions, Languages */}
                  {!showAddContentOnly && getToShowRegionForRecentFeed() &&
                    !isPublicTransport &&
                    !useLanguageRegionDropDown && (
                      <>
                        <Select
                          ignorePadding
                          isDisabled={
                            regionLanguageList.filter(
                              (regionLanguage) => !regionLanguage.hidden
                            ).length === 1
                          }
                          value={selectedRegionLanguage}
                          options={regionLanguageList.filter(
                            (regionLanguage) => !regionLanguage.hidden
                          )}
                          onChange={(value: any) => {
                            onRegionLanguageChange(value);
                          }}
                          styles={CustomSelectStyles as any}
                          components={RegionSelectComponents as any}
                        />
                      </>
                    )}

                  {/* Famous Quotes */}
                  {showRegions && useLanguageRegionDropDown && (
                    <>
                      <Select
                        ignorePadding
                        isDisabled={
                          regionLanguageList.filter(
                            (regionLanguage) => !regionLanguage.hidden
                          ).length === 1
                        }
                        value={selectedRegionLanguage}
                        options={regionLanguageList.filter(
                          (regionLanguage) => !regionLanguage.hidden
                        )}
                        onChange={(value: any) => {
                          onRegionLanguageChange(value);
                        }}
                        styles={CustomSelectStyles as any}
                        components={RegionLanguageSelectComponents as any}
                      />
                    </>
                  )}

                  {isNationalWeather && (
                    <>
                      <Select
                        ignorePadding
                        isDisabled={disableRegions}
                        value={selectedRegionLanguage}
                        options={regionLanguageList.filter(
                          (regionLanguage) => !regionLanguage.hidden
                        )}
                        onChange={onNationalWeatherChange}
                        styles={CustomSelectStyles as any}
                        components={RegionSelectComponents as any}
                      />

                      <Select
                        ignorePadding
                        value={selectedLanguage}
                        options={localWeatherLanguageList}
                        onChange={(value) => {
                          onLanguageChange(value);
                        }}
                        styles={CustomSelectStyles as any}
                        components={LanguageSelectComponents as any}
                      />
                    </>
                  )}

                  {(isFamousBirthday || isTodayInHistory) &&
                    channel &&
                    channel.custom_fields &&
                    channel.custom_fields.timezone && (
                      <>
                        <Select
                          ignorePadding
                          value={
                            selectedTimeZone
                              ? timezonesList.find(
                                  (_item) => _item.value === selectedTimeZone
                                )
                              : timezonesList[0]
                          }
                          options={timezonesList}
                          onChange={onTimezoneChange}
                          styles={CustomSelectStyles as any}
                          components={TimeZoneSelectComponents as any}
                        />
                      </>
                    )}

                  {/* Weather forecast */}
                  {isLocalWeather && (
                    <>
                      <AsyncSelect
                        title={
                          customFields && customFields.code
                            ? "feeds.city_name"
                            : t("feeds.enter_a_city_name")
                        }
                        icon={
                          <SelectIcon name="locationPin" iconSize="medium" />
                        }
                        loadOptions={
                          loadAsyncPlacesOptions({ citiesOnly: true })
                        }
                        onChange={async (value: any) => {
                          const _data = {
                            lat: value.lat,
                            long: value.long,
                            name: value.label,
                            country: value.country,
                            place_id: value.place_id,
                            city: value.city,
                            location: !isAirQuality
                              ? `${value.main_text}${
                                  value.secondary_text
                                    ? `, ${value.secondary_text}`
                                    : ""
                                }`
                              : undefined,
                            main_text: value.main_text,
                            secondary_text: value.secondary_text,
                            languages: value.languages,
                          };
                          setApiCityLanguageList(value.languages);
                          setCustomFields((prev: any) =>
                            prev ? { ...prev, ..._data } : { ..._data }
                          );
                        }}
                        scaleAmount={SCALE_AMOUNT}
                        withDescription
                        withIcon
                        noOptionsMessage={() => null}
                        styles={{
                          loadingIndicator: (base) => ({
                            ...base,
                            marginRight: 10,
                            transform: `scale(1.2)`,
                          }),
                        }}
                        components={{
                          DropdownIndicator: null,
                          Option: OptionWithMedia("auto"),
                          Menu: SelectMenuComponent({ title: "Cities" }) as any,
                        }}
                      />

                      {customFields && customFields.languages && (
                        <Select
                          ignorePadding
                          value={selectedLanguage}
                          options={localWeatherLanguageList}
                          onChange={(value) => {
                            onLanguageChange(value);
                          }}
                          styles={CustomSelectStyles as any}
                          components={LanguageSelectComponents as any}
                        />
                      )}
                    </>
                  )}

                  {/* Air Quality */}
                  {isAirQuality && (
                    <>
                    <AsyncSelect
                      title={
                        customFields && customFields.code
                          ? "Location"
                          : t("feeds.enter_your_location")
                      }
                      icon={<SelectIcon name="locationPin" iconSize="medium" />}
                      loadOptions={loadAsyncPlacesOptions({
                        citiesOnly: false,
                      })}
                      onChange={async ({
                        value,
                        long,
                        lat,
                        label,
                        city,
                        country,
                        main_text,
                        secondary_text,
                        place_id,
                        languages,
                        place,
                      }: any) => {

                        const _data = {
                          long,
                          lat,
                          name: label,
                          country,
                          place_id,
                          place,
                          city,
                          pin: 1,
                          location: `${main_text}${
                            secondary_text ? `, ${secondary_text}` : ""
                          }`,
                          main_text: main_text,
                          secondary_text: secondary_text,
                          languages,
                        };

                        const _languages = await fetch(
                          `https://api.countrylayer.com/v2/name/${replaceCountry(
                            country
                          )}?access_key=8f035ba86fc2a94b4c169a650d4150bf&FullText=`
                        );

                        const data = await _languages.json();

                        if (data?.[0].languages) {
                          languages = data[0].languages;
                        }

                        setApiCityLanguageList(languages);

                        setCustomFields((prev: any) => {
                          return prev
                            ? {
                                ...prev,
                                location: `${city}, ${country}`,
                                languages: languages,
                                lat: lat,
                                lon: long,
                              }
                            : {
                                location: `${city}, ${country}`,
                                languages: languages,
                                lat: lat,
                                lon: long,
                              };
                        });
                      }}
                      scaleAmount={SCALE_AMOUNT}
                      withDescription
                      withIcon
                      noOptionsMessage={() => null}
                      styles={{
                        loadingIndicator: (base) => ({
                          ...base,
                          marginRight: 10,
                          transform: `scale(1.2)`,
                        }),
                      }}
                      components={{
                        DropdownIndicator: null,
                        Option: OptionWithMedia("auto"),
                        Menu: SelectMenuComponent({
                          title: "Locations",
                        }) as any,
                      }}
                    />
                    {customFields && customFields.languages && (
                      <Select
                        ignorePadding
                        value={selectedLanguage}
                        options={localWeatherLanguageList}
                        onChange={(value) => {
                          onLanguageChange(value);
                        }}
                        styles={CustomSelectStyles as any}
                        components={LanguageSelectComponents as any}
                      />
                    )}
                    </>
                  )}

                  {/* Custom stocks */}
                  {isStocks && isArray(customFields) && (
                    <>
                      {customFields.map((field, index) => {
                        return (
                          <>
                            <CustomSelectContainer
                              position="relative"
                              key={index}
                            >
                              <AsyncSelect
                                title={t("feeds.stock_name")}
                                loadOptions={loadAsyncStockOptions}
                                onChange={(value: any) => {
                                  setCustomFields((prev: any) => {
                                    prev[index] = {
                                      id: value.value,
                                      name: `${value.label}, ${value.description}`,
                                      isAvailableWhenAdded: value.available,
                                    };
                                    return [...prev];
                                  });
                                }}
                                scaleAmount={SCALE_AMOUNT}
                                withDescription
                                noOptionsMessage={() => null}
                                styles={{
                                  loadingIndicator: (base) => ({
                                    ...base,
                                    marginRight: index > 0 ? 42 : 10,
                                    transform: `scale(1.2)`,
                                  }),
                                }}
                                components={{
                                  DropdownIndicator: null,
                                  Option: OptionWithMedia("auto"),
                                  Menu: SelectMenuComponent({
                                    title: "Stocks",
                                  }) as any,
                                }}
                              />
                              {index > 0 && (
                                <PinBlock>
                                  <Button
                                    icon="cross"
                                    iconProps={{ iconSize: "smaller" }}
                                    buttonType="transparent"
                                    fontSize="smallest"
                                    elementHeightBase={false}
                                    elementWidth="block"
                                    opacity={0.5}
                                    onClick={() => {
                                      setCustomFields([
                                        ...customFields.filter(
                                          (d, i) => index !== i
                                        ),
                                      ]);
                                    }}
                                  />
                                </PinBlock>
                              )}
                            </CustomSelectContainer>
                          </>
                        );
                      })}

                      {customFields[0] && size(customFields) <= 3 && (
                        <>
                          <Hr />
                          <AddMoreRowsButton
                            onClick={() =>
                              setCustomFields([...customFields, ""])
                            }
                          >
                            <Icon name="add" color="cta" iconSize="smallest" />
                            Add more stocks
                          </AddMoreRowsButton>
                        </>
                      )}
                    </>
                  )}

                  {/* Traffic */}
                  {isTraffic && (
                    <AsyncSelect
                      title={
                        customFields && customFields.code
                          ? "Location"
                          : t("feeds.enter_your_location")
                      }
                      icon={<SelectIcon name="locationPin" iconSize="medium" />}
                      loadOptions={loadAsyncPlacesOptions({
                        citiesOnly: false,
                      })}
                      onChange={({
                        long,
                        lat,
                        label,
                        city,
                        country,
                        main_text,
                        secondary_text,
                        place_id,
                        languages,
                        place,
                      }: any) => {
                        const _data = {
                          long,
                          lat,
                          name: label,
                          country,
                          place_id,
                          place,
                          city,
                          pin: 1,
                          location: `${main_text}${
                            secondary_text ? `, ${secondary_text}` : ""
                          }`,
                          main_text: main_text,
                          secondary_text: secondary_text,
                          languages,
                        };

                        console.log(languages);

                        setApiCityLanguageList(languages);
                        const languageOption = {
                          label: languages[0].name,
                          slug: languages[0].iso639_1.toLocaleUpperCase(),
                          value: languages[0].iso639_1,
                        };
                        onLanguageChange(languageOption);

                        setCustomFields((prev: any) => {
                          return prev ? { ...prev, ..._data } : { ..._data };
                        });
                      }}
                      scaleAmount={SCALE_AMOUNT}
                      withDescription
                      withIcon
                      noOptionsMessage={() => null}
                      styles={{
                        loadingIndicator: (base) => ({
                          ...base,
                          marginRight: 10,
                          transform: `scale(1.2)`,
                        }),
                      }}
                      components={{
                        DropdownIndicator: null,
                        Option: OptionWithMedia("auto"),
                        Menu: SelectMenuComponent({
                          title: "Locations",
                        }) as any,
                      }}
                    />
                  )}

                  {/* Station  */}
                  {isTransportInfo && (
                    <AsyncSelect
                      title={
                        customFields && customFields.code
                          ? "Location"
                          : t("feeds.enter_your_location")
                      }
                      icon={<SelectIcon name="locationPin" iconSize="medium" />}
                      loadOptions={loadAsyncPlacesOptions({
                        citiesOnly: false,
                      })}
                      onChange={(props) => {
                        const _data = {
                          long: props.long,
                          lat: props.lat,
                          name: props.label,
                          country: props.country,
                          place_id: props.place_id,
                          city: props.city,
                          location: `${props.main_text}${
                            props.secondary_text
                              ? `, ${props.secondary_text}`
                              : ""
                          }`,
                          main_text: props.main_text,
                          secondary_text: props.secondary_text,
                          languages: props.languages,
                        };

                        setApiCityLanguageList(props.languages);
                        const languageOption = {
                          label: props.languages[0].name,
                          slug: props.languages[0].iso639_1.toLocaleUpperCase(),
                          value: props.languages[0].iso639_1,
                        };
                        onLanguageChange(languageOption);

                        setCustomFields((prev: any) => {
                          return prev ? { ...prev, ..._data } : { ..._data };
                        });

                        if ("lat" in props && "long" in props) {
                          onTransportInfoChange(props.lat, props.long);
                        }
                      }}
                      scaleAmount={SCALE_AMOUNT}
                      withDescription
                      withIcon
                      noOptionsMessage={() => null}
                      styles={{
                        loadingIndicator: (base) => ({
                          ...base,
                          marginRight: 10,
                          transform: `scale(1.2)`,
                        }),
                      }}
                      components={{
                        DropdownIndicator: null,
                        Option: OptionWithMedia("auto"),
                        Menu: SelectMenuComponent({
                          title: "Locations",
                        }) as any,
                      }}
                    />
                  )}

                  {customFields &&
                    !isNationalWeather &&
                    !isLocalWeather &&
                    !isAirQuality &&
                    customFields.languages && (
                      <>
                        <Select
                          ignorePadding
                          value={selectedLanguage}
                          options={localWeatherLanguageList}
                          onChange={(value) => {
                            onLanguageChange(value);
                          }}
                          styles={CustomSelectStyles as any}
                          components={LanguageSelectComponents as any}
                        />
                          {!isPublicTransport && !isTransportInfo && !isTraffic && <Hr />}
                        </>
                    )}
                  {isTransportInfo && transportList?.length > 0 && (
                    <>
                      <Select
                        ignorePadding
                        value={
                          selectedPlace
                            ? transportList.find(
                                (_item) => _item.value === selectedPlace
                              )
                            : transportList[0]
                        }
                        options={transportList}
                        onChange={onPlaceChange}
                        styles={CustomSelectStyles as any}
                        components={TransportSelectComponents as any}
                      />
                    </>
                  )}

                  {isTraffic && customFields.place_id && (
                    <>
                      {isArray(customFields.destinations) &&
                        customFields.destinations.map((dest: any, key: any) => (
                          <React.Fragment key={key}>
                            <CustomSelectContainer
                              position="relative"
                              gridTemplate={{ columns: `1fr` }}
                            >
                              <AsyncSelect
                                defaultValue={{ label: get(dest, "to.name") }}
                                title={
                                  customFields && customFields.destinations[key]
                                    ? `${getDestinationOrderPrefix(
                                        key + 1
                                      )} Destination`
                                    : t(
                                        "feeds.feeds.enter_your_destination_city"
                                      )
                                }
                                icon={
                                  <SelectIcon
                                    name="locationPin"
                                    iconSize="medium"
                                  />
                                }
                                loadOptions={loadAsyncPlacesOptions({
                                  citiesOnly: true,
                                })}
                                onChange={({ long, lat, label: name }) => {
                                  setCustomFields((prev: any) => {
                                    const destination = generateDestination({
                                      prev,
                                      long,
                                      lat,
                                      name,
                                    });
                                    prev.destinations[key] = destination;
                                    return { ...prev };
                                  });
                                }}
                                scaleAmount={SCALE_AMOUNT}
                                withDescription
                                withIcon
                                noOptionsMessage={() => null}
                                styles={{
                                  loadingIndicator: (base) => ({
                                    ...base,
                                    marginRight: 42,
                                    transform: `scale(1.2)`,
                                  }),
                                }}
                                components={{
                                  DropdownIndicator: null,
                                  Option: OptionWithMedia("auto"),
                                  Menu: SelectMenuComponent({
                                    title: "Cities",
                                  }) as any,
                                }}
                              />

                              <PinBlock>
                                <Button
                                  icon="cross"
                                  iconProps={{ iconSize: "smaller" }}
                                  buttonType="transparent"
                                  fontSize="smallest"
                                  elementHeightBase={false}
                                  elementWidth="block"
                                  opacity={0.5}
                                  onClick={() => {
                                    setCustomFields({
                                      ...customFields,
                                      destinations:
                                        customFields.destinations.filter(
                                          (d: any, i: any) => key !== i
                                        ),
                                    });
                                  }}
                                />
                              </PinBlock>
                            </CustomSelectContainer>
                          </React.Fragment>
                        ))}

                      {/*size(customFields.destinations) <= 3 && (
                        <>
                          <AddMoreRowsButton onClick={addNewDestination}>
                            <Icon name="add" color="cta" iconSize="smallest" />
                            {t("feeds.add_desination")}
                          </AddMoreRowsButton>
                        </>
                      )*/}
                    </>
                  )}
                  <SliderSubmitButton
                    {...props}
                    canSubmit={
                      isTraffic &&
                      !("place_id" in props.data?.custom_fields) &&
                      props.status !== "DONE"
                        ? false
                        : isLocalWeather &&
                          !("place_id" in props.data?.custom_fields) &&
                          props.status !== "DONE"
                        ? false
                        : isPublicTransport &&
                          !("place" in props.data?.custom_fields) &&
                          props.status !== "DONE"
                        ? false
                        : (isPublicTransport ||
                            isNationalWeather ||
                            isTransportInfo) &&
                          props.status !== "IN_PROGRESS"
                        ? true
                        : props.canSubmit
                    }
                    status={
                      isPublicTransport &&
                      !("place" in props.data?.custom_fields)
                        ? "lat" in props.data?.custom_fields &&
                          transportList.length === 0
                          ? "NO_STATION_FOUND"
                          : (isPublicTransport || isTransportInfo) &&
                            (props.status === "DISABLED" ||
                              props.status === "DONE")
                          ? "DONE"
                          : "INIT"
                        : props.status
                    }
                  />
                </>
              ) : (
                <SliderUnlockButton {...props} />
              )}
            </Block>
          </SliderItemOnHoverContentInner>
        </SliderItemOnHoverContent>
      </SliderItemOnHoverContainer>
    </>
  );
};

export default SliderActiveItem;
